import { useCallback } from 'react';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { Module } from '../../../../../api/module';
import { FileMedialink, getSignedUrl, getSignedUrlForPreview } from '../../../../../api/medialink';
import { getFileTypeDescription } from '../../../../../utils/fileType';
import getAccessToken from '../../../../../api/BearerToken';
import JWPlayerModal from '../../components/modals/JWPlayerModal';
import { MedialinkDialogHeaderTags } from '../../../components/MedialinkDialogHeaderTags';
import PdfPreviewModal from '../../components/modals/PdfPreviewModal';
import useLicenses from '../../../../../hooks/useLicenses';
import ImagePreviewModal from '../../components/modals/ImagePreviewModal';
import NoPreviewModal from '../../components/modals/NoPreviewModal';

export default function useFileMedialink() {
  const handleError = useThrowAsyncError();
  const { hasOnlyDemoProductLicenseForTeacher } = useLicenses();

  const openFileMedialink = useCallback(
    async (material: FileMedialink, module: Module) => {
      const {
        file: { s3file: file },
      } = material;

      const fileType = getFileTypeDescription(file.mimeType).key;

      const accessToken = await getAccessToken();

      const linkUrl = `${process.env.REACT_APP_P_API_URL}/api/s3-files/${file.id}/link`;

      const urlWithAccessToken = `${linkUrl}?${new URLSearchParams({ access_token: accessToken }).toString()}`;

      const downloadUrl = `${linkUrl}?${new URLSearchParams({
        access_token: accessToken,
        attachment: material.name,
      }).toString()}`;

      const hasOnlyDemoLicenseForTeacher = hasOnlyDemoProductLicenseForTeacher(module);

      const downloadable = !hasOnlyDemoLicenseForTeacher && material.file.forceDownload;

      const titleTooltip = material.file.description ? `${material.name}: ${material.file.description}` : material.name;

      try {
        if (file.preview) {
          const { url: signedUrlForPreview } = await getSignedUrlForPreview(file.id);

          return (
            <PdfPreviewModal
              id="pdf-preview-modal"
              url={signedUrlForPreview}
              downloadUrl={downloadable ? downloadUrl : undefined}
              linkTarget={material.file.pdfLinkTarget}
              headerProps={{
                title: material.name,
                tags: <MedialinkDialogHeaderTags material={material} module={module} />,
                titleTooltip,
              }}
            />
          );
        }

        switch (fileType) {
          case 'IMAGE':
            return (
              <ImagePreviewModal
                image={{ src: urlWithAccessToken, alt: material.name }}
                id="image-preview-modal"
                downloadUrl={downloadable ? downloadUrl : undefined}
                headerProps={{
                  title: material.name,
                  titleTooltip,
                  tags: <MedialinkDialogHeaderTags material={material} module={module} />,
                }}
              />
            );

          case 'PDF':
            const { url: signedUrlPdf } = await getSignedUrl(material.id);

            return (
              <PdfPreviewModal
                id="pdf-preview-modal"
                url={signedUrlPdf}
                downloadUrl={downloadable ? downloadUrl : undefined}
                linkTarget={material.file.pdfLinkTarget}
                headerProps={{
                  title: material.name,
                  tags: <MedialinkDialogHeaderTags material={material} module={module} />,
                  titleTooltip,
                }}
              />
            );

          case 'AUDIO':
          case 'VIDEO':
            const { url: signedUrlJWPlayer } = await getSignedUrl(material.id);

            return (
              <JWPlayerModal
                id={`jwplayer-${fileType.toLocaleLowerCase()}-modal`}
                media={material.jwplayer?.ready ? { playlist: signedUrlJWPlayer } : { file: signedUrlJWPlayer }}
                downloadUrl={downloadable ? downloadUrl : undefined}
                headerProps={{
                  title: material.name,
                  tags: <MedialinkDialogHeaderTags material={material} module={module} />,
                  titleTooltip,
                }}
                audioOnly={fileType === 'AUDIO'}
              />
            );
        }

        return (
          <NoPreviewModal
            id="no-preview-modal"
            downloadUrl={downloadUrl}
            headerProps={{
              title: material.name,
              tags: <MedialinkDialogHeaderTags material={material} module={module} />,
              titleTooltip,
            }}
          />
        );
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, hasOnlyDemoProductLicenseForTeacher],
  );

  return { openFileMedialink };
}
